import React from "react";
import { PublicLayout } from "../components/PublicLayout";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="mb-3 font-display text-xl font-bold tracking-tight text-zinc-950">
      {title}
    </h2>
    <div className="space-y-3 text-sm leading-relaxed text-zinc-700">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  const lastUpdated = "May 2, 2026";
  return (
    <PublicLayout
      eyebrow="Legal"
      title="Privacy Policy"
      subtitle={`Last updated: ${lastUpdated}`}
    >
      <p className="mb-8 text-sm leading-relaxed text-zinc-700">
        Megatron ("we", "our", or "the app") is an Instagram automation tool that
        helps Instagram creators send automated public replies and direct messages
        in response to comments containing specific keywords. This Privacy Policy
        explains what limited data we process, how we use it, and your rights.
      </p>

      <Section title="1. Information We Collect">
        <p>
          We process only the minimum data necessary to deliver automated replies.
          Specifically, when a comment is posted on a connected Instagram account,
          Meta's Graph API delivers the following fields to Megatron:
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            <strong>Instagram comment ID</strong> — used to publish a reply to that
            specific comment
          </li>
          <li>
            <strong>Comment text</strong> — used to match against your configured
            keywords
          </li>
          <li>
            <strong>Commenter's Instagram username and Instagram-Scoped User ID
            (IGSID)</strong> — used to send a direct message in response
          </li>
          <li>
            <strong>Media (post) ID</strong> — used to scope keywords to specific posts
          </li>
          <li>
            <strong>Timestamp of the comment</strong> — used for activity logs and
            to enforce Meta's 24-hour messaging window
          </li>
        </ul>
        <p>
          We do <strong>not</strong> collect: emails, phone numbers, payment
          information, browsing history, follower lists, or any data outside what
          Instagram delivers in the comments webhook payload.
        </p>
      </Section>

      <Section title="2. How We Use Your Information">
        <ul className="ml-5 list-disc space-y-1">
          <li>To match comment text against keywords configured by the account owner</li>
          <li>To post a public reply to the comment via Meta's Graph API</li>
          <li>To send a direct message to the commenter via Meta's Graph API</li>
          <li>
            To maintain a private activity log visible only to the connected
            account owner, for debugging and analytics
          </li>
        </ul>
      </Section>

      <Section title="3. What We Do NOT Do">
        <ul className="ml-5 list-disc space-y-1">
          <li>
            We do <strong>not</strong> sell, rent, or share your personal data with
            third parties
          </li>
          <li>
            We do <strong>not</strong> use your data for advertising, profiling,
            or training machine-learning models
          </li>
          <li>
            We do <strong>not</strong> read or store private DMs between users
            (only DMs explicitly delivered by Instagram's webhook to the connected
            account)
          </li>
          <li>
            We do <strong>not</strong> store credentials in plaintext in code or
            in version control — your access tokens are kept in encrypted database
            fields
          </li>
        </ul>
      </Section>

      <Section title="4. Data Storage & Retention">
        <p>
          Activity logs (comment IDs, commenter usernames, comment text, reply
          status, DM status) are stored in our private MongoDB database. The
          connected account owner can clear this log at any time via the Megatron
          Activity dashboard ("Clear ALL activity" button). We retain logs only
          for as long as the account owner finds them useful — there is no
          mandatory retention period.
        </p>
        <p>
          Access tokens, App Secrets, and Webhook Verify Tokens are stored in our
          settings database and are accessible only to the authenticated dashboard
          user.
        </p>
      </Section>

      <Section title="5. Third-Party Services">
        <p>
          Megatron interacts with one third-party service:{" "}
          <strong>Meta's Instagram Graph API</strong>{" "}
          (<a
            href="https://developers.facebook.com/docs/instagram-api"
            className="text-amber-600 underline"
            target="_blank"
            rel="noreferrer"
          >
            graph.instagram.com
          </a>
          ). Comment delivery, reply posting, and DM sending are all routed through
          Meta. Their privacy practices are governed by{" "}
          <a
            href="https://www.facebook.com/privacy/policy/"
            className="text-amber-600 underline"
            target="_blank"
            rel="noreferrer"
          >
            Meta's Privacy Policy
          </a>
          .
        </p>
      </Section>

      <Section title="6. Your Rights">
        <ul className="ml-5 list-disc space-y-1">
          <li>
            <strong>Right to access</strong> — request a copy of any data we hold
            about you
          </li>
          <li>
            <strong>Right to deletion</strong> — see our{" "}
            <a href="/data-deletion" className="text-amber-600 underline">
              Data Deletion Instructions
            </a>
          </li>
          <li>
            <strong>Right to revoke access</strong> — disconnect Megatron at any
            time via your Instagram <em>Apps and websites</em> settings
          </li>
          <li>
            <strong>Right to object</strong> — opt out of processing by removing
            the comment that triggered automation
          </li>
        </ul>
      </Section>

      <Section title="7. Contact">
        <p>
          Questions about this policy or your data? Email us at{" "}
          <a href="mailto:connect@yogarubika.com" className="text-amber-600 underline">
            connect@yogarubika.com
          </a>
          .
        </p>
      </Section>

      <Section title="8. Changes to This Policy">
        <p>
          We may update this policy occasionally. The "Last updated" date at the
          top of this page reflects the most recent revision. Continued use of
          Megatron after a change constitutes acceptance of the updated policy.
        </p>
      </Section>
    </PublicLayout>
  );
}
